import React, { useMemo, useState } from 'react';
import io from 'socket.io-client';
import { useBeforeunload } from 'react-beforeunload';

// utils
import {
  getSingleConversationFromLocalStorage,
  IConversation,
} from 'utils/conversations';
// configs
import { apiExpress, isEnvDevelopment } from 'configs/api';
// types
import { ISocket, ISOCKET_ACTIONS } from './types';

const config = {
  path: '/socket.io',
  rejectUnauthorized: false,
  reconnectionAttempts: 5,
  reconnectionDelay: 2000,
  reconnectionDelayMax: 10000,
  transports: ['websocket'],
};

interface ISocketContext {
  socket: ISocket | null;
  currentConversation: IConversation | undefined;
  setCurrentConversation: React.Dispatch<
    React.SetStateAction<IConversation | undefined>
  >;
}
export const SocketContext = React.createContext({} as ISocketContext);

const URL_PATH = isEnvDevelopment ? 'https://localhost:3000' : apiExpress;

export const SocketProvider = ({
  children,
}: {
  children?: React.ReactNode;
}) => {
  const [socket, setSocket] = useState<ISocket | null>(null);
  const [currentConversation, setCurrentConversation] = useState(() =>
    getSingleConversationFromLocalStorage()
  );

  useBeforeunload(() => {
    socket?.emit(ISOCKET_ACTIONS.DISCONNECT_CONVERSATION, {
      conversationId: Number(currentConversation?.conversationId),
      userId: currentConversation?.userId,
    });
    socket?.disconnect();
  });

  React.useEffect(() => {
    if (socket === null) {
      setSocket(io(URL_PATH, config));
    }
  }, []);

  // socket on connect
  React.useEffect(() => {
    const conversationId = currentConversation?.conversationId;
    if (socket === null || !conversationId) {
      return;
    }

    socket.on(ISOCKET_ACTIONS.CONNECT, () => {
      socket?.emit(ISOCKET_ACTIONS.CONNECT_CONVERSATION, {
        conversationId: Number(conversationId),
        userId: currentConversation?.userId,
      });
    });

    socket.on(ISOCKET_ACTIONS.DISCONNECT, () => {
      console.log('socket disconnected');
    });
    return () => {
      socket?.disconnect();
    };
  }, [socket]);

  const value = useMemo(
    () => ({ socket, currentConversation, setCurrentConversation }),
    [socket, currentConversation]
  );

  return (
    <SocketContext.Provider value={value}>{children}</SocketContext.Provider>
  );
};

export const useSockets = () => {
  const context = React.useContext(SocketContext);
  if (context === undefined) {
    throw new Error('useSockets must be used within a SocketProvider');
  }
  return context;
};
